import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { usePeersStore, useRoomStore, useNotificationStore, useMagicPenStore } from '@src/stores';
import { useRoomContext } from '@src/contexts';
import SocketService from '@src/services/SocketService';

import { createIcon } from '@src/helpers';

import { AnnotationIcon } from '@src/assets/icons';

import { ECommonSocketKeys, EUserType } from '@src/types';

type Participant = {
  id: string;
  name: string;
  avatarUrl?: string | null;
  initials?: string;
  isHost?: boolean;
  isCoHost?: boolean;
  isActingHost?: boolean; // True if has ACTING_HOST permission (but not actual HOST type)
  isScreenSharer?: boolean;
  peerId?: string;
};

interface FetchPeersPermissionsResponse {
  success: boolean;
  data: Record<string, string[]>;
}

const useInviteToMagicPenModal = () => {
  const { currentPeerId, shareScreenStream, isCurrentPeerSharing, isHost, isActingHost } = useRoomContext();

  const { isInviteToMagicPenModal, setInviteToMagicPenModal } = useRoomStore(
    useShallow((state) => ({
      isInviteToMagicPenModal: state.isInviteToMagicPenModal,
      setInviteToMagicPenModal: state.setInviteToMagicPenModal,
    }))
  );

  const { setAnnotatorIds } = useMagicPenStore(
    useShallow((state) => ({
      setAnnotatorIds: state.setAnnotatorIds,
    }))
  );

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  // Get peers without shallow comparison to ensure we detect deep changes
  const {
    peers: peersMap,
    shouldRefetchPeerPermissions,
    setShouldRefetchPeerPermissions,
  } = usePeersStore(
    useShallow((state) => ({
      peers: state.peers,
      shouldRefetchPeerPermissions: state.shouldRefetchPeerPermissions,
      setShouldRefetchPeerPermissions: state.setShouldRefetchPeerPermissions,
    }))
  );

  const screenSharerPeerIds = shareScreenStream?.peerId;

  const [peersPermissions, setPeersPermissions] = useState<Record<string, string[]>>({});

  const { participants } = useMemo(() => {
    const peers = Array.from(peersMap.values());

    const participantsList: Participant[] = peers
      .filter((p) => ![p.id, p.peer_info?.peer_id].includes(currentPeerId))
      .map((peer) => {
        const peerId = String(peer.id || peer.peer_info?.peer_id);
        const peerUuid = String(peer.peer_uuid || peer.peer_info?.peer_uuid);

        // Check if peer is a host by user_type or has ACTING_HOST permission
        const isHostByType = peer.peer_info?.user_type === EUserType.HOST;
        const hasActingHostPermission =
          peersPermissions[peerId]?.includes('ACTING_HOST') ??
          peersPermissions[peerUuid]?.includes('ACTING_HOST') ??
          false;
        const isHost = isHostByType || hasActingHostPermission;
        // isActingHost is true if they have ACTING_HOST permission but are not an actual HOST
        const isActingHost = hasActingHostPermission && !isHostByType;
        const isScreenSharer = screenSharerPeerIds === peer.id;
        const isCoHost = peer.peer_info?.user_type === EUserType.CO_HOST;

        return {
          id: peerUuid, // Use UUID as the ID for selection
          name: String(peer.peer_info?.peer_name || 'Unknown'),
          isHost,
          isActingHost,
          isScreenSharer,
          peerId,
          isCoHost,
        };
      });

    return {
      participants: participantsList,
    };
  }, [currentPeerId, peersMap, screenSharerPeerIds, peersPermissions]);

  const initialSelectedIdsRef = useRef<string[]>([]);
  const uuidToNameMapRef = useRef<Map<string, string>>(new Map());

  // Update UUID to name mapping when participants change
  useEffect(() => {
    participants.forEach((p) => {
      uuidToNameMapRef.current.set(p.id, p.name);
    });
  }, [participants]);

  const initialAllowAll = false;

  const [query, setQuery] = useState('');
  const [allowAll, setAllowAll] = useState(initialAllowAll);
  const [isLoading, setIsLoading] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(() => new Set<string>(initialSelectedIdsRef.current));
  const [confirmedSelectedIds, setConfirmedSelectedIds] = useState<string[]>([]);

  const filteredUser = useMemo(() => {
    const key = query.trim().toLowerCase();
    let filtered = key ? participants.filter((peer) => peer.name.toLowerCase().includes(key)) : participants;

    // Sort: disabled annotators (hosts and screen sharers) first, then others alphabetically
    return filtered.sort((a, b) => {
      const aIsDisabled = a.isHost || a.isScreenSharer;
      const bIsDisabled = b.isHost || b.isScreenSharer;

      // If one is disabled and the other isn't, disabled comes first
      if (aIsDisabled && !bIsDisabled) return -1;
      if (!aIsDisabled && bIsDisabled) return 1;

      // If both are disabled or both are not disabled, sort by name alphabetically
      return a.name.localeCompare(b.name);
    });
  }, [participants, query]);

  const selectedCount = useMemo(
    () => (allowAll ? participants.length : selected.size),
    [allowAll, participants.length, selected.size]
  );

  const toggleOne = useCallback(
    (id: string, v: boolean) => {
      // Check if the participant is a host or screen sharer (should not be toggleable)
      const participant = participants.find((p) => p.id === id);
      const isHostOrScreenSharer = participant?.isHost || participant?.isScreenSharer;

      // Don't allow deselecting hosts or screen sharers
      if (isHostOrScreenSharer && !v) {
        return;
      }

      setSelected((prev) => {
        const next = new Set(prev);
        if (v) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [participants]
  );

  // Automatically ensure hosts and screen sharers are always selected (when modal is open)
  useEffect(() => {
    if (!isInviteToMagicPenModal) return;

    const hostsAndScreenSharers = participants.filter((p) => p.isHost || p.isScreenSharer).map((p) => p.id);

    if (hostsAndScreenSharers.length > 0) {
      setSelected((prev) => {
        const next = new Set(prev);
        hostsAndScreenSharers.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [participants, isInviteToMagicPenModal]);

  useEffect(() => {
    const shouldFetch =
      isInviteToMagicPenModal || shouldRefetchPeerPermissions || isCurrentPeerSharing || isHost || isActingHost;

    if (!shouldFetch) return;

    const fetchPeersPermissions = async () => {
      setIsLoading(true);
      try {
        // Get current values from stores/context inside the effect to avoid dependency issues
        const currentPeersMap = usePeersStore.getState().peers;
        const currentScreenSharerPeerId = shareScreenStream?.peerId;

        const response: FetchPeersPermissionsResponse = await SocketService.request(
          ECommonSocketKeys.FETCH_PEERS_PERMISSIONS,
          {}
        );

        if (response?.success && response?.data) {
          // Store permissions data to check for ACTING_HOST and other permissions
          setPeersPermissions(response.data);

          // Build identifierToUuid map from current peers (mapping both socket IDs and UUIDs to UUIDs)
          const identifierToUuid = new Map<string, string>();
          const currentPeersSet = Array.from(currentPeersMap.values());
          currentPeersSet.forEach((peer) => {
            const peerId = String(peer.id || peer.peer_info?.peer_id);
            const peerUuid = String(peer.peer_uuid || peer.peer_info?.peer_uuid);
            if (peerId) identifierToUuid.set(peerId, peerUuid);
            if (peerUuid) identifierToUuid.set(peerUuid, peerUuid);
          });

          // Extract identifiers (can be socket IDs or UUIDs) that have the MAGIC_PEN_ALLOW_ANNOTATE permission (from response)
          const identifiersWithPermission = Object.keys(response.data).filter((identifier) =>
            response.data[identifier].includes('MAGIC_PEN_ALLOW_ANNOTATE')
          );

          // Convert identifiers to UUIDs for selection
          const peerUuidsWithPermission = identifiersWithPermission
            .map((id) => identifierToUuid.get(id))
            .filter((uuid): uuid is string => uuid !== undefined);

          // Calculate hosts and screen sharers directly from peers and permissions data
          const hostsAndScreenSharersUuids = currentPeersSet
            .filter((p) => ![p.id, p.peer_info?.peer_id].includes(currentPeerId))
            .filter((peer) => {
              const peerId = String(peer.id || peer.peer_info?.peer_id);
              const peerUuid = String(peer.peer_uuid || peer.peer_info?.peer_uuid);
              const isHostByType = peer.peer_info?.user_type === EUserType.HOST;
              const hasActingHostPerm =
                (response.data[peerId]?.includes('ACTING_HOST') || response.data[peerUuid]?.includes('ACTING_HOST')) ??
                false;
              const isHostPeer = isHostByType || hasActingHostPerm;
              const isScreenSharerPeer = currentScreenSharerPeerId === peer.id;
              return isHostPeer || isScreenSharerPeer;
            })
            .map((peer) => String(peer.peer_uuid || peer.peer_info?.peer_uuid))
            .filter((uuid): uuid is string => uuid !== undefined);

          // Combine all selected participants (existing permissions + hosts + screen sharers)
          const allSelectedUuids = Array.from(new Set([...peerUuidsWithPermission, ...hostsAndScreenSharersUuids]));

          // Update the selected state and initial ref
          initialSelectedIdsRef.current = allSelectedUuids;
          setSelected(new Set(allSelectedUuids));
          setConfirmedSelectedIds(allSelectedUuids);
        }
      } catch (error) {
        console.error('Failed to fetch peers permissions:', error);
      } finally {
        setIsLoading(false);
        // Reset the flag after fetching
        if (shouldRefetchPeerPermissions) {
          setShouldRefetchPeerPermissions(false);
        }
      }
    };

    fetchPeersPermissions();
  }, [
    isInviteToMagicPenModal,
    shouldRefetchPeerPermissions,
    isCurrentPeerSharing,
    isHost,
    isActingHost,
    currentPeerId,
    shareScreenStream,
    setShouldRefetchPeerPermissions,
  ]);

  // Sync annotatorIds with the store based on confirmed (server-verified) selection
  useEffect(() => {
    const confirmedSet = new Set(confirmedSelectedIds);
    const annotatorPeerIds = participants
      .filter((p) => confirmedSet.has(p.id) || p.isHost || p.isScreenSharer)
      .map((p) => p.peerId!)
      .filter(Boolean);

    if (isHost || isActingHost || isCurrentPeerSharing) {
      if (!annotatorPeerIds.includes(currentPeerId)) {
        annotatorPeerIds.push(currentPeerId);
      }
    }

    setAnnotatorIds(Array.from(new Set(annotatorPeerIds)));
  }, [confirmedSelectedIds, participants, isHost, isActingHost, isCurrentPeerSharing, currentPeerId, setAnnotatorIds]);

  // Helper function to create annotation notifications
  const createAnnotationNotification = useCallback(
    (text: string) => {
      appendNotification({
        title: '',
        text,
        source: 'annotation',
        icon: createIcon({ icon: AnnotationIcon, size: '28px' }),
      });
    },
    [appendNotification]
  );

  const handleUpdate = useCallback(() => {
    const selectedPeerUuids = Array.from(selected);
    const previousSelectedUuids = initialSelectedIdsRef.current;
    const validParticipantUuids = new Set(participants.map((p) => p.id));

    // Filter out disconnected peers
    const validSelectedPeerUuids = selectedPeerUuids.filter((uuid) => validParticipantUuids.has(uuid));

    // If some peers disconnected, update state and show error
    if (validSelectedPeerUuids.length !== selectedPeerUuids.length) {
      const disconnectedUuids = selectedPeerUuids.filter((uuid) => !validParticipantUuids.has(uuid));
      const disconnectedNames = disconnectedUuids
        .map((uuid) => uuidToNameMapRef.current.get(uuid) || 'Attendee')
        .join(', ');

      setSelected(new Set(validSelectedPeerUuids));
      createAnnotationNotification(`Could not update permissions for ${disconnectedNames}, Please try again.`);
      if (validSelectedPeerUuids.length === 0) return;
    }

    // Hosts and active screen sharers should never be sent as part of magic pen permissions payload
    const nonConfigurablePeerUuids = new Set(participants.filter((p) => p.isHost || p.isScreenSharer).map((p) => p.id));

    // Only configurable peers are actually included in the permission payload
    const permissionPayloadPeerUuids = validSelectedPeerUuids.filter((uuid) => !nonConfigurablePeerUuids.has(uuid));

    // Compare current vs previous selection, but only for configurable peers
    const granted = permissionPayloadPeerUuids.filter((uuid) => !previousSelectedUuids.includes(uuid));
    const revoked = previousSelectedUuids.filter(
      (uuid) => !nonConfigurablePeerUuids.has(uuid) && !permissionPayloadPeerUuids.includes(uuid)
    );

    SocketService.request(ECommonSocketKeys.MAGIC_PEN_PERMISSION, { peerUUIds: permissionPayloadPeerUuids })
      .then((response: { success: boolean }) => {
        if (!response?.success) {
          createAnnotationNotification('Could not update permissions. Please try again.');
          return;
        }

        // Sync annotatorIds with the confirmed selection
        setConfirmedSelectedIds(validSelectedPeerUuids);

        // Show notifications to the host based on changes
        const totalChanges = granted.length + revoked.length;

        if (totalChanges === 0) {
          // No changes made - close modal without notification
          initialSelectedIdsRef.current = validSelectedPeerUuids;
          setInviteToMagicPenModal(false);
          return;
        }

        // Generate notification message based on change count
        let notificationText: string;
        if (totalChanges === 1) {
          // Single person changed
          const changedUuid = granted.length > 0 ? granted[0] : revoked[0];
          const isGranted = granted.length > 0;
          const participant = participants.find((p) => p.id === changedUuid);
          const participantName = participant?.name || 'Attendee';
          notificationText = isGranted
            ? `Annotation enabled for ${participantName}`
            : `Annotation disabled for ${participantName}`;
        } else {
          // Multiple people changed - show bulk notification
          notificationText = 'Annotation permissions updated successfully';
        }

        createAnnotationNotification(notificationText);

        // Update the initial ref with the new selection (only valid peers)
        initialSelectedIdsRef.current = validSelectedPeerUuids;
        setInviteToMagicPenModal(false);
      })
      .catch((error) => {
        // Handle error (e.g., network error, disconnected attendee)
        console.error('Failed to update annotation permissions:', error);
        createAnnotationNotification('Could not update permissions. Please try again.');
      });
  }, [selected, setInviteToMagicPenModal, createAnnotationNotification, participants]);

  const handleClose = () => {
    setInviteToMagicPenModal(false);
    setQuery('');
    setAllowAll(initialAllowAll);
    setSelected(new Set(initialSelectedIdsRef.current));
  };

  // Filter out hosts and screen sharers from selectable participants
  const selectableFilteredUsers = useMemo(
    () => filteredUser.filter((p) => !p.isHost && !p.isScreenSharer),
    [filteredUser]
  );

  // Check if there are any disabled items (hosts/screen sharers) in the filtered view
  const hasDisabledItems = useMemo(() => filteredUser.some((p) => p.isHost || p.isScreenSharer), [filteredUser]);

  const allVisibleSelected = useMemo(() => {
    // If there are no selectable users, return false
    if (selectableFilteredUsers.length === 0) return false;

    // If there are disabled items present, never show full check (always indeterminate)
    if (hasDisabledItems) return false;

    // All selectable (non-disabled) users must be selected
    return selectableFilteredUsers.every((x) => selected.has(x.id));
  }, [selectableFilteredUsers, selected, hasDisabledItems]);

  const noneVisibleSelected = useMemo(
    () => selectableFilteredUsers.length === 0 || selectableFilteredUsers.every((x) => !selected.has(x.id)),
    [selectableFilteredUsers, selected]
  );

  const someVisibleSelected = useMemo(() => {
    // If there are disabled items and any selectable items are selected, show indeterminate
    if (hasDisabledItems && selectableFilteredUsers.some((x) => selected.has(x.id))) {
      return true;
    }

    // Normal indeterminate logic (some but not all selectable items selected)
    return !allVisibleSelected && !noneVisibleSelected;
  }, [allVisibleSelected, noneVisibleSelected, hasDisabledItems, selectableFilteredUsers, selected]);

  const toggleAllVisible = (v: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      selectableFilteredUsers.forEach((p) => (v ? next.add(p.id) : next.delete(p.id)));
      return next;
    });
  };

  const handleSummaryCheck = () => {
    // Check if all selectable items are currently selected (ignoring allVisibleSelected which is false when disabled items exist)
    const allSelected = selectableFilteredUsers.length > 0 && selectableFilteredUsers.every((x) => selected.has(x.id));

    if (allSelected) {
      // If all selectable items are selected, deselect them all
      toggleAllVisible(false);
    } else {
      // Otherwise, select all selectable items
      toggleAllVisible(true);
    }
  };

  return {
    isInviteToMagicPenModal,
    selectedCount,
    allowAll,
    setAllowAll,
    query,
    setQuery,
    filteredUser,
    selected,
    toggleOne,
    handleUpdate,
    handleClose,
    allVisibleSelected,
    someVisibleSelected,
    handleSummaryCheck,
    isLoading,
  };
};

export default useInviteToMagicPenModal;
